import { queryOptions } from '@tanstack/react-query'
import { z } from 'zod'
import { FinancialCacheSchema } from '@shared/contracts/financial-cache'
import { apiFetch } from '@shared/api/http'
import { CurrencySchema } from '@shared/contracts/enums'
import { AnalyticsRateSchema, AnalyticsWindowSchema } from '@shared/contracts/seller-analytics'
import { MonetaryValueSchema } from '@shared/lib/money/money'

const SliceSchema = z.object({
  subscriptions: z.number(),
  charges: z.number(),
  amount: MonetaryValueSchema,
})
export const SubscriptionMetricsSchema = z.object({
  cache: FinancialCacheSchema.optional(),
  window: AnalyticsWindowSchema.extend({ baseAt: z.string() }),
  primaryCurrency: CurrencySchema,
  currencies: z.array(CurrencySchema),
  byCurrency: z.record(
    z.string(),
    z.object({
      mrr: MonetaryValueSchema,
      activeSubscriptions: z.number(),
      overdueSubscriptions: z.number().nullable(),
      churn: AnalyticsRateSchema.extend({ cancellations: z.number() }),
      projection: z.object({
        paid: SliceSchema,
        scheduled: SliceSchema,
        recovery: SliceSchema,
        remaining: SliceSchema,
        previousRecovery: SliceSchema,
      }),
      ltv: z.object({
        value: MonetaryValueSchema.nullable(),
        reason: z.enum(['NO_ACTIVE_BASE', 'INSUFFICIENT_HISTORY', 'NO_CHURN']).nullable(),
        arpu: MonetaryValueSchema,
        churn: AnalyticsRateSchema,
        historyMonths: z.number(),
        historyFrom: z.string(),
        historyTo: z.string(),
      }),
    }),
  ),
})
export type SubscriptionMetrics = z.infer<typeof SubscriptionMetricsSchema>
export function subscriptionMetricsQuery(query = '', enabled = true) {
  return queryOptions({
    queryKey: ['subscriptions', 'metrics', query] as const,
    enabled,
    queryFn: async () =>
      SubscriptionMetricsSchema.parse(await apiFetch<unknown>(`/dashboard/subscriptions?${query}`)),
  })
}
